import { z } from 'zod'
import { LOCATION_LEVELS } from './location-policy'

/** Namespaced document providers (spec §3.1): never a biometric id; `fake-doc` exists outside production only. */
export const DOCUMENT_PROVIDER_IDS = ['didit-doc', 'idanalyzer-doc', 'fake-doc'] as const
export const DocumentProviderIdSchema = z.enum(DOCUMENT_PROVIDER_IDS)
export type DocumentProviderId = z.infer<typeof DocumentProviderIdSchema>

/** Launch classes, global in the PoC (spec §7). Residence permits and non-identity cards are excluded. */
export const DOCUMENT_CLASSES = ['passport', 'id_card', 'driver_licence'] as const
export type DocumentClass = (typeof DOCUMENT_CLASSES)[number]

export const PROVIDER_VALIDATION_STATES = ['unvalidated', 'api_verified', 'smoke_verified'] as const
export type ProviderValidation = (typeof PROVIDER_VALIDATION_STATES)[number]

export const Iso3Schema = z.string().regex(/^[A-Z]{3}$/)
/** Off by default with a reason code (spec §7 restricted list, privacy research §1). */
export const RESTRICTED_COUNTRIES: Readonly<Record<string, string>> = { DEU: 'pauswg_20', NLD: 'ap_id_copy_rules' }

const DEFAULT_SIDES: Record<DocumentClass, 1 | 2> = { passport: 1, id_card: 2, driver_licence: 2 }
/**
 * Per-country launch rules (spec §7): ISR ID front only (never the appendix), PSE ID one side and no expiry,
 * ISR and PSE driver licences both sides (the PSE licence is unvalidated until the real-document smoke scan, R22).
 */
const CLASS_RULES: Readonly<
  Record<
    string,
    {
      sides?: Partial<Record<DocumentClass, 1 | 2>>
      heldBack?: DocumentClass[]
      noExpiry?: DocumentClass[]
      noMrz?: DocumentClass[]
    }
  >
> = {
  ISR: { sides: { id_card: 1 }, noMrz: ['id_card', 'driver_licence'] },
  PSE: { sides: { id_card: 1 }, noExpiry: ['id_card'], noMrz: ['id_card'] },
}
export const sidesFor = (iso3: string, c: DocumentClass): 1 | 2 => CLASS_RULES[iso3]?.sides?.[c] ?? DEFAULT_SIDES[c]
export const heldBack = (iso3: string): DocumentClass[] => [...(CLASS_RULES[iso3]?.heldBack ?? [])]
export const hasExpiry = (iso3: string, c: DocumentClass): boolean => !CLASS_RULES[iso3]?.noExpiry?.includes(c)
/** Passports always carry an MRZ; driver licences never (in the launch set); ID cards unless listed. */
export const hasMrz = (iso3: string, c: DocumentClass): boolean =>
  c === 'passport' ? true : c === 'driver_licence' ? false : !CLASS_RULES[iso3]?.noMrz?.includes(c)

/**
 * Review media (2026-11-03 review-media spec §2, ruling R4): the days the photos of a registration in review are kept at
 * most once the member enters review (deleted earlier at the decision). An admin setting, 2..14, default 7.
 */
export const REVIEW_MEDIA_HOLD_DAYS = { min: 2, max: 14, default: 7 } as const
export const ReviewMediaHoldDaysSchema = z
  .number()
  .int()
  .min(REVIEW_MEDIA_HOLD_DAYS.min)
  .max(REVIEW_MEDIA_HOLD_DAYS.max)

export const DocumentPolicySchema = z.object({
  /** Settings checkbox "Require an identity document" (R-D7). Ships false; review-first when true (R-D1). */
  required: z.boolean().default(false),
  defaultProvider: DocumentProviderIdSchema.default('didit-doc'),
  providerByCountry: z.record(Iso3Schema, DocumentProviderIdSchema).default({}),
  exemptions: z.record(Iso3Schema, z.object({ level: z.enum(LOCATION_LEVELS) })).default({}),
  /** Processing matrix: every catalogue country (Israel and Palestine included) is ON unless listed here with a reason code. */
  countriesOff: z.record(Iso3Schema, z.string().regex(/^[a-z0-9_]{1,40}$/)).default({ ...RESTRICTED_COUNTRIES }),
  /** Review media hold (R4); a stored policy without it reads as the default. */
  reviewMediaHoldDays: ReviewMediaHoldDaysSchema.default(REVIEW_MEDIA_HOLD_DAYS.default),
  version: z.number().int().min(1).default(1),
})
export type DocumentPolicy = z.infer<typeof DocumentPolicySchema>
export const DEFAULT_DOCUMENT_POLICY: DocumentPolicy = DocumentPolicySchema.parse({})

/**
 * A partial update of the policy. A record given here replaces the stored record as a whole (send the full map: an
 * entry left out is removed), as the location policy patch does. `expectedVersion`, when sent, must equal the stored
 * version (an admin editing a stale copy gets 409 `conflict` instead of overwriting a newer change).
 */
export const DocumentPolicyPatchSchema = z.strictObject({
  expectedVersion: z.number().int().min(1).optional(),
  required: z.boolean().optional(),
  defaultProvider: DocumentProviderIdSchema.optional(),
  providerByCountry: z.record(Iso3Schema, DocumentProviderIdSchema).optional(),
  exemptions: z.record(Iso3Schema, z.object({ level: z.enum(LOCATION_LEVELS) })).optional(),
  countriesOff: z.record(Iso3Schema, z.string().regex(/^[a-z0-9_]{1,40}$/)).optional(),
  reviewMediaHoldDays: ReviewMediaHoldDaysSchema.optional(),
})
export type DocumentPolicyPatch = z.infer<typeof DocumentPolicyPatchSchema>

/** What the member pages need (spec §4.1: the step count follows the public policy). */
export const PublicDocumentPolicySchema = z.object({
  required: z.boolean(),
  steps: z.union([z.literal(4), z.literal(5)]),
})
/** Days the photos kept for a review are held at most (review-media spec R4: default 7, range 2..14). */
export const REVIEW_MEDIA_HOLD_DAYS_DEFAULT = REVIEW_MEDIA_HOLD_DAYS.default
export const flowSteps = (required: boolean): 4 | 5 => (required ? 5 : 4)

export const CHECK_STATES = ['passed', 'failed', 'not_applicable', 'not_run', 'unknown'] as const
export type CheckState = (typeof CHECK_STATES)[number]
export const CheckStateSchema = z.enum(CHECK_STATES)

export const DOCUMENT_EXEMPTION_STATES = ['current', 'consumed', 'invalidated'] as const
export type DocumentExemptionState = (typeof DOCUMENT_EXEMPTION_STATES)[number]

/** `members.document_check` (spec §8.2, clear codes). */
export const DocumentCheckSchema = z.object({
  v: z.literal(1),
  basis: z.enum(['document', 'exempt', 'not_required', 'pre_document', 'manual']),
  status: z.enum(['checked', 'not_checked', 'stale', 'verified_manually']),
  method: z.enum(['provider', 'video_call']).nullable(),
  verifiedDay: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable(),
  policyVersion: z.number().int().nullable(),
})
export type DocumentCheck = z.infer<typeof DocumentCheckSchema>

/**
 * `members.manual_verification` (spec §9, §10.3, §13 "Verified manually"; clear codes and ids only): the method (a
 * supervised video call), the admin who verified, when, and the `document_*` / `face_reference_*` reasons it resolved.
 */
export const ManualVerificationSchema = z.object({
  v: z.literal(1),
  method: z.literal('video_call'),
  reviewerId: z.uuid(),
  at: z.iso.datetime({ offset: true }),
  resolvedReasons: z.array(z.string().regex(/^[a-z_]{1,64}$/)),
})
export type ManualVerification = z.infer<typeof ManualVerificationSchema>
